<div class="cmt-avatar" aria-hidden="true">{{ initials() }}</div>
<div class="cmt-body">
  <header class="cmt-meta">
    <span class="author">
      @if (comment().author.isDeleted) {
        {{ 'COMMENTS.AUTHOR_DELETED' | translate }}
      } @else {
        {{ authorLabel() }}
      }
    </span>
    @if (isVisuallyUnread()) {
      <span class="cmt-unread-pill" [attr.aria-label]="'COMMENTS.UNREAD_ARIA' | translate">
        {{ 'COMMENTS.UNREAD_PILL' | translate }}
      </span>
    }
    <time
      class="time"
      [attr.datetime]="comment().createdAt.toISOString()"
      [attr.title]="comment().createdAt | date: 'M/d/yyyy, h:mm a'">
      {{ relativeTime() }}
    </time>
    @if (comment().isEdited && !comment().isDeleted) {
      <span class="edited">
        @if (comment().isModerated) {
          ({{ 'COMMENTS.EDITED_BY_MODERATOR' | translate }})
        } @else {
          ({{ 'COMMENTS.EDITED' | translate }})
        }
      </span>
    }
  </header>

  @if (replyToAuthor()) {
    <div class="cmt-replyto">
      <i class="pi pi-angle-right arr" aria-hidden="true"></i>
      <span>{{ 'COMMENTS.IN_REPLY_TO' | translate }}</span>
      @if (replyToAuthor()!.isDeleted) {
        <strong>{{ 'COMMENTS.AUTHOR_DELETED' | translate }}</strong>
      } @else {
        <strong>&#64;{{ replyToAuthor()!.firstName }} {{ replyToAuthor()!.lastName }}</strong>
      }
    </div>
  }

  @if (comment().isDeleted) {
    <div class="cmt-bubble">
      <i class="pi pi-trash" aria-hidden="true"></i>
      <span>
        @if (comment().isDeletedByModerator) {
          {{ 'COMMENTS.DELETED_BY_MODERATOR' | translate }}
        } @else {
          {{ 'COMMENTS.DELETED' | translate }}
        }
      </span>
    </div>
  } @else if (isEditing()) {
    <tf-comment-edit-form
      [comment]="comment()"
      [requireReason]="editRequiresReason()"
      (saved)="editSaved.emit($event)"
      (cancelled)="editCancelled.emit()"
      (draftChange)="editDraftChange.emit($event)" />
  } @else {
    @if (comment().mentions.length === 0) {
      <!-- Fast path: no mentions in the body — render through the comments-specific marked +
           DOMPurify pipeline. Allows inline images (`<img>` with `attachment:` or `https:` src)
           and the click delegation on the wrapper routes them to the preview dialog. The wrapper
           itself isn't interactive; the renderer marks each `<img>` with `tabindex=0` +
           `role=button`, so the focusable surface lives on the children. -->
      <!-- eslint-disable-next-line @angular-eslint/template/interactive-supports-focus -->
      <div
        class="cmt-md"
        [innerHTML]="renderedBodyHtml()"
        (click)="onBodyClick($event)"
        (keydown)="onBodyKeydown($event)"></div>
    } @else {
      <!-- Mention path: render alternating text + chip segments. Text runs go through the
           inline markdown pass (`renderCommentInlineText`) so the serializer's hard-break
           backslashes render as `<br>` and inline emphasis / escapes resolve, while the chips
           stay as Angular components (keeping their detail popovers). Block markdown inside an
           inter-mention run is still not processed — the alternative (interleaving block HTML)
           wraps each chunk in `<p>` and strips the boundary whitespace around the chips. -->
      <div class="cmt-md">
        @for (segment of bodySegments(); track $index) {
          @if (segment.kind === 'text') {
            <span class="cmt-md__segment-text" [innerHTML]="segment.html"></span>
          } @else {
            <tf-mention-chip [mention]="segment.mention" />
          }
        }
      </div>
    }
  }

  @if (!comment().isDeleted && !isEditing()) {
    <tf-attachments-row
      [attachments]="comment().attachments"
      (attachmentClick)="attachmentOpenRequested.emit($event)" />
  }

  @if (!comment().isDeleted && !isEditing() && hasReactions()) {
    <tf-reactions-bar
      [reactions]="comment().reactions"
      [canReact]="canReact()"
      (reactionToggled)="onReactionToggled($event)"
      (addReactionRequested)="onOpenReactionPicker($event)" />
  }

  @if (hasActions() && !isEditing()) {
    <div class="cmt-actions">
      @if (canReact()) {
        <button
          type="button"
          class="cmt-action cmt-action--icon-only"
          [attr.aria-label]="'COMMENTS.REACTIONS.ADD_ARIA' | translate"
          [attr.title]="'COMMENTS.REACTIONS.ADD_ARIA' | translate"
          (click)="onOpenReactionPicker($event)">
          <tf-react-icon />
          <span class="sr-only">{{ 'COMMENTS.REACTIONS.ADD_ARIA' | translate }}</span>
        </button>
        <span class="cmt-action-sep" aria-hidden="true"></span>
      }
      @if (canReply() && !comment().isDeleted) {
        <button
          type="button"
          class="cmt-action"
          (click)="replyRequested.emit(comment().id)">
          <i class="pi pi-angle-right" aria-hidden="true"></i>
          <span>{{ 'COMMENTS.REPLY' | translate }}</span>
        </button>
      }
      @if (canEdit()) {
        <span class="cmt-action-sep" aria-hidden="true"></span>
        <button
          type="button"
          class="cmt-action"
          (click)="editRequested.emit(comment().id)">
          <i class="pi pi-pencil" aria-hidden="true"></i>
          <span>{{ 'COMMENTS.MENU.EDIT' | translate }}</span>
        </button>
      }
      @if (canDelete()) {
        <span class="cmt-action-sep" aria-hidden="true"></span>
        <button
          type="button"
          class="cmt-action cmt-action--danger cmt-action--icon-only"
          [attr.title]="'COMMENTS.MENU.DELETE' | translate"
          (click)="deleteRequested.emit(comment().id)">
          <i class="pi pi-trash" aria-hidden="true"></i>
          <!-- Label stays in the DOM for screen readers; the `--icon-only` modifier hides it visually. -->
          <span class="sr-only">{{ 'COMMENTS.MENU.DELETE' | translate }}</span>
        </button>
      }
      @if (canRestore()) {
        <span class="cmt-action-sep" aria-hidden="true"></span>
        <button
          type="button"
          class="cmt-action"
          (click)="restoreRequested.emit(comment().id)">
          <i class="pi pi-replay" aria-hidden="true"></i>
          <span>{{ 'COMMENTS.MENU.RESTORE' | translate }}</span>
        </button>
      }
      @if (canViewHistory()) {
        <span class="cmt-action-sep" aria-hidden="true"></span>
        <button
          type="button"
          class="cmt-action cmt-action--icon-only"
          [attr.title]="'COMMENTS.MENU.EDIT_HISTORY' | translate"
          (click)="viewHistoryRequested.emit(comment().id)">
          <i class="pi pi-history" aria-hidden="true"></i>
          <span class="sr-only">{{ 'COMMENTS.MENU.EDIT_HISTORY' | translate }}</span>
        </button>
      }
      @if (canCopyLink()) {
        <span class="cmt-action-sep" aria-hidden="true"></span>
        <button
          type="button"
          class="cmt-action cmt-action--icon-only"
          [attr.title]="'COMMENTS.MENU.COPY_LINK' | translate"
          (click)="copyLinkRequested.emit(comment().id)">
          <i class="pi pi-link" aria-hidden="true"></i>
          <span class="sr-only">{{ 'COMMENTS.MENU.COPY_LINK' | translate }}</span>
        </button>
      }
      @if (canMarkRead() || canMarkUnread()) {
        <span class="cmt-action-sep" aria-hidden="true"></span>
        @if (canMarkRead()) {
          <button
            type="button"
            class="cmt-action"
            (click)="markReadRequested.emit(comment().id)">
            <i class="pi pi-check-circle" aria-hidden="true"></i>
            <span>{{ 'COMMENTS.MENU.MARK_READ' | translate }}</span>
          </button>
        } @else {
          <button
            type="button"
            class="cmt-action"
            (click)="markUnreadRequested.emit(comment().id)">
            <i class="pi pi-circle" aria-hidden="true"></i>
            <span>{{ 'COMMENTS.MENU.MARK_UNREAD' | translate }}</span>
          </button>
        }
      }
    </div>
  }

  <tf-reaction-picker #reactionPicker (emojiSelected)="onPickerEmojiSelected($event)" />
</div>
